import type { BetSource } from '@mobi/betslip/types'
import type { QuickbetSelection } from '@core/Areas/Quickbet/types'
import type { LoadBetDetails } from '@core/Areas/ShareBet/LoadBet/types'
import { LoadBetErrors } from '@core/Areas/ShareBet/LoadBet/constants'
import { buildBetSelection } from './buildBetSelection'

/** ONLY used when limited or stale data is available for selection
 *  Used in areas such as Share Bet, Re-Bet and Bet Loop
 */
export const loadBet = async (
  betPayload: LoadBetDetails[],
  betSource: { location?: BetSource; origin?: string; copiedBetLoopPostId?: string } = {}
): Promise<LoadBetReturn> => {
  try {
    const isMulti = betPayload.length > 1

    const builtSelections = await Promise.allSettled(
      betPayload.map(x => buildBetSelection(x, { shouldRemoveScratched: isMulti }))
    )

    const selections = builtSelections.flatMap(builtSelection => {
      if (builtSelection.status === 'fulfilled') {
        builtSelection.value.betSource = betSource.location
        builtSelection.value.betSourceShareOrigin = betSource.origin
        builtSelection.value.copiedBetLoopPostId = betSource.copiedBetLoopPostId
        return [builtSelection.value]
      }
      return []
    })

    const { successful, failed } = builtSelections.reduce<SuccessFailedCounts>(
      (acc, curr) => {
        if (curr.status === 'fulfilled') acc.successful += 1
        if (curr.status === 'rejected') acc.failed += 1
        return acc
      },
      { successful: 0, failed: 0 }
    )

    if (selections.length === 0) {
      const failedSelections = builtSelections.filter(selection => selection.status === 'rejected')
      throw failedSelections.length > 0
        ? failedSelections[0].reason
        : new Error(LoadBetErrors.Default)
    }

    return { selections, successful, failed }
  } catch (error) {
    throw isErrorInstance(error) && error.message === LoadBetErrors.BettingClosed
      ? new Error(LoadBetErrors.BettingClosed)
      : new Error(LoadBetErrors.Default)
  }
}

// =============
// Local Helpers
// =============

const isErrorInstance = (error: unknown): error is Error => error instanceof Error

// =====
// Types
// =====

type LoadBetReturn = {
  selections: QuickbetSelection[]
} & SuccessFailedCounts

type SuccessFailedCounts = {
  successful: number
  failed: number
}
